import React, { InputHTMLAttributes, ReactNode, RefObject, useMemo } from "react";
import { IValidator, validateClassName, randomId } from "@sebgroup/extract";
import GreenFormItem from "../GreenFormItem";
import { NumberInputProps, InputProps } from "./types";

export const InputWrapper = ({ children, unitLabel }: { children: React.ReactNode; unitLabel?: string }) => (
  <>
    {unitLabel ? (
      <div className="group group-border group-focus textbox">
        {children}
        <span className="form-text">{unitLabel}</span>
      </div>
    ) : (
      <div className="gds-input-wrapper">{children}</div>
    )}
  </>
);

export interface RendererProps {
  id?: string;
  type: string;
  inputMode?: "email" | "tel" | "search" | "text" | "url" | "none" | "numeric" | "decimal" | undefined;
  name: string;
  props: InputHTMLAttributes<HTMLInputElement>;
  onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  inputRef?: RefObject<HTMLInputElement>;
  label?: string;
  info?: ReactNode | string;
  unitLabel?: string;
  validator?: IValidator;
  slideoutButtonLabel?: string;
  onSlideoutButtonClick?: () => void;
  testId?: string;
  footer?: ReactNode;
}

export type Renderer = (props: RendererProps) => React.JSX.Element;

export const RenderInput: Renderer = ({
  id,
  type,
  inputMode,
  name,
  props,
  onBlur,
  onChange,
  inputRef,
  label,
  info,
  unitLabel,
  validator,
  slideoutButtonLabel,
  onSlideoutButtonClick,
  testId,
  footer,
}: RendererProps) => {
  const generatedId = useMemo(() => randomId(), []);
  const inputId = id ?? generatedId;

  const propsWithDescription = info || validator ? { ...props, "aria-describedby": validator ? `${inputId}_error` : `${inputId}_info` } : props;
  const inputProps = { ...propsWithDescription, onBlur, onChange };

  // Render naked
  if (!label && !info) return <input ref={inputRef} id={inputId} type={type} name={name} data-testid={testId} {...inputProps} />;

  return (
    <GreenFormItem
      validator={validator}
      labelInformation={info}
      label={label}
      slideoutButtonLabel={slideoutButtonLabel}
      onSlideoutButtonClick={onSlideoutButtonClick}
      inputId={inputId}
      name={name}
      footer={footer}
    >
      <InputWrapper unitLabel={unitLabel}>
        <input
          ref={inputRef}
          id={id}
          name={name}
          type={type}
          inputMode={inputMode}
          {...inputProps}
          className={validator && validateClassName(validator?.indicator)}
          data-testid={testId}
          aria-invalid={!!validator}
        />
      </InputWrapper>
    </GreenFormItem>
  );
};

export const GreenNumberInput = ({
  id,
  name,
  label,
  info,
  onBlur,
  onChange,
  inputRef,
  validator,
  slideoutButtonLabel,
  onSlideoutButtonClick,
  testId,
  numberType,
  footer,
  unitLabel,
  ...props
}: NumberInputProps & { footer?: ReactNode }) =>
  RenderInput({
    id,
    type: numberType,
    inputMode: "numeric",
    name,
    props,
    onBlur,
    onChange,
    inputRef,
    label,
    info,
    unitLabel,
    validator,
    slideoutButtonLabel,
    onSlideoutButtonClick,
    testId,
    footer,
  });

export const GreenTextInput = ({
  id,
  name,
  label,
  info,
  onChange,
  inputRef,
  validator,
  slideoutButtonLabel,
  onSlideoutButtonClick,
  testId,
  footer,
  ...props
}: InputProps & { footer?: ReactNode }) =>
  RenderInput({
    id,
    type: "text",
    name,
    props,
    onChange,
    inputRef,
    label,
    info,
    validator,
    slideoutButtonLabel,
    onSlideoutButtonClick,
    testId,
    footer,
  });
